import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { ICoordenadas } from 'modelos/src';
import { MapboxService, TipoMapaBase } from '../mapbox.service';
import {
  coordenadasToGeoJSONPolygon,
  latLngToLngLat,
} from '../mapbox-coords';

/**
 * Mapa Mapbox de solo lectura. Muestra un marcador puntual y/o polígonos.
 * Reemplaza los `<google-map>` read-only (detalles-sirena, preview del wizard).
 */
@Component({
  selector: 'app-mapa-display',
  standalone: true,
  template: `
    <div class="mapa-display-wrapper" [style.height]="height()" [style.width]="width()">
      <div #cont class="mapa-display-cont"></div>
      <button
        type="button"
        class="mapa-display-toggle"
        (click)="toggleEstilo()"
        title="Cambiar tipo de mapa"
      >
        {{ estilo === 'calle' ? 'Satélite' : 'Calle' }}
      </button>
    </div>
  `,
  styles: [
    `
      .mapa-display-wrapper {
        position: relative;
      }
      .mapa-display-cont {
        width: 100%;
        height: 100%;
        border-radius: 8px;
        overflow: hidden;
      }
      .mapa-display-toggle {
        position: absolute;
        top: 8px;
        left: 8px;
        z-index: 1;
        border: none;
        border-radius: 4px;
        padding: 4px 10px;
        background: #fff;
        color: #0c4767;
        font-size: 12px;
        cursor: pointer;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
      }
    `,
  ],
})
export class MapaDisplayComponent implements AfterViewInit, OnDestroy {
  private mapboxService = inject(MapboxService);

  private readonly cont =
    viewChild.required<ElementRef<HTMLDivElement>>('cont');

  readonly marcador = input<ICoordenadas | undefined>(undefined);
  readonly poligonos = input<ICoordenadas[][]>([]);
  readonly zoom = input<number>(16);
  readonly height = input<string>('350px');
  readonly width = input<string>('100%');
  readonly estiloInicial = input<TipoMapaBase>('calle');

  public estilo: TipoMapaBase = 'calle';

  private map?: mapboxgl.Map;
  private marker?: mapboxgl.Marker;
  private cargado = false;
  private resizeObserver?: ResizeObserver;

  private readonly SRC_POLIGONOS = 'display-poligonos';

  constructor() {
    // Reacciona a cambios de inputs una vez que el mapa cargó.
    effect(() => {
      const marcador = this.marcador();
      const poligonos = this.poligonos();
      if (this.cargado) {
        this.renderMarcador(marcador);
        this.renderPoligonos(poligonos);
        this.encuadrar();
      }
    });
  }

  ngAfterViewInit(): void {
    this.estilo = this.estiloInicial();
    this.map = this.mapboxService.crearMapa(this.cont().nativeElement, {
      center: this.marcador() ?? this.centroPoligonos(),
      zoom: this.zoom(),
      estilo: this.estilo,
    });
    this.map.on('load', () => {
      this.cargado = true;
      this.agregarCapasPoligonos();
      this.renderMarcador(this.marcador());
      this.renderPoligonos(this.poligonos());
      this.encuadrar();
    });
    // Al cambiar de estilo, Mapbox limpia sources/layers: re-agregarlas.
    this.map.on('style.load', () => {
      if (this.cargado) {
        this.agregarCapasPoligonos();
        this.renderPoligonos(this.poligonos());
      }
    });
    // Re-dimensionar si el contenedor arranca/cambia de tamaño (evita mapa en blanco).
    this.resizeObserver = new ResizeObserver(() => this.map?.resize());
    this.resizeObserver.observe(this.cont().nativeElement);
  }

  toggleEstilo(): void {
    this.estilo = this.estilo === 'calle' ? 'satelite' : 'calle';
    this.map?.setStyle(this.mapboxService.estiloUrl(this.estilo));
  }

  private agregarCapasPoligonos(): void {
    if (!this.map || this.map.getSource(this.SRC_POLIGONOS)) {
      return;
    }
    this.map.addSource(this.SRC_POLIGONOS, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    this.map.addLayer({
      id: `${this.SRC_POLIGONOS}-fill`,
      type: 'fill',
      source: this.SRC_POLIGONOS,
      paint: { 'fill-color': '#0c4767', 'fill-opacity': 0.15 },
    });
    this.map.addLayer({
      id: `${this.SRC_POLIGONOS}-line`,
      type: 'line',
      source: this.SRC_POLIGONOS,
      paint: { 'line-color': '#0c4767', 'line-width': 2 },
    });
  }

  private renderMarcador(coord?: ICoordenadas): void {
    if (!this.map) {
      return;
    }
    if (!coord) {
      this.marker?.remove();
      this.marker = undefined;
      return;
    }
    const lngLat = latLngToLngLat(coord);
    if (this.marker) {
      this.marker.setLngLat(lngLat);
    } else {
      this.marker = new mapboxgl.Marker().setLngLat(lngLat).addTo(this.map);
    }
  }

  private renderPoligonos(poligonos: ICoordenadas[][]): void {
    const source = this.map?.getSource(this.SRC_POLIGONOS) as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (!source) {
      return;
    }
    source.setData({
      type: 'FeatureCollection',
      features: (poligonos || [])
        .filter((p) => p?.length)
        .map((p) => coordenadasToGeoJSONPolygon([p])),
    });
  }

  /** Encadra el mapa a los polígonos (si hay) o centra en el marcador. */
  private encuadrar(): void {
    if (!this.map) {
      return;
    }
    const puntos = (this.poligonos() || []).flat();
    if (puntos.length) {
      const bounds = new mapboxgl.LngLatBounds();
      puntos.forEach((c) => bounds.extend(latLngToLngLat(c)));
      this.map.fitBounds(bounds, { padding: 40, maxZoom: 16, duration: 0 });
    } else if (this.marcador()) {
      this.map.setCenter(latLngToLngLat(this.marcador()!));
    }
  }

  private centroPoligonos(): ICoordenadas | undefined {
    const puntos = (this.poligonos() || []).flat();
    if (!puntos.length) {
      return undefined;
    }
    const sum = puntos.reduce(
      (a, c) => ({ lat: a.lat + c.lat, lng: a.lng + c.lng }),
      { lat: 0, lng: 0 },
    );
    return { lat: sum.lat / puntos.length, lng: sum.lng / puntos.length };
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.marker?.remove();
    this.map?.remove();
  }
}
